// frontend/src/components/common/SubscriptionGuard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

const DEFAULT_CORPORATE_NOTICE = (
  <>
    For corporate users, check if your team already has access, or contact{' '}
    <a href="mailto:info@drivenby.com.hk" style={{ color: 'inherit', textDecoration: 'underline' }}>
      info@drivenby.com.hk
    </a>{' '}
    directly for enterprise options.
  </>
);

const SubscriptionGuard = ({
  routeId,
  tier,
  children,
  featureName,
  description,
  corporateNotice,
  onLoginClick,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { checkAccess } = useSubscription();
  const [access, setAccess] = useState(null);

  const refreshAccess = useCallback(async () => {
    if (isAuthenticated && routeId) {
      if (user?.isAdmin) {
        setAccess(true);
        return;
      }
      const newAccess = await checkAccess(routeId, tier);
      setAccess(newAccess);
    }
  }, [isAuthenticated, routeId, tier, checkAccess, user]);

  useEffect(() => {
    refreshAccess();
  }, [refreshAccess]);

  // Wrapper for blurred content + overlay
  const BlurredContent = ({ message, button, footnote }) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
      <div style={{ height: '100vh', overflow: 'auto', filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--overlay)',
          padding: 20,
          textAlign: 'center',
          pointerEvents: 'auto',
        }}
      >
        {message}
        {button}
        {footnote}
      </div>
    </div>
  );

  // Not authenticated – show login/register links
  if (!isAuthenticated) {
    return (
      <BlurredContent
        message={
          <>
            <div style={{ color: 'var(--text-on-overlay)', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Please log in to access {featureName}
            </div>
            <div style={{ color: 'var(--text-on-overlay-muted)', fontSize: 14, maxWidth: 400, marginBottom: 12 }}>
              {description}
            </div>
          </>
        }
        button={
          <div style={{ color: 'var(--text-on-overlay-muted)', fontSize: 14 }}>
            <button
              onClick={() => onLoginClick('login')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-hover)',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Login
            </button>
            {' or '}
            <button
              onClick={() => onLoginClick('register')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-hover)',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Register
            </button>
          </div>
        }
        footnote={
          <div
            style={{
              color: 'var(--text-on-overlay-muted)',
              fontSize: 12,
              maxWidth: 400,
              marginTop: 16,
              opacity: 0.85,
            }}
          >
            {corporateNotice || DEFAULT_CORPORATE_NOTICE}
          </div>
        }
      />
    );
  }

  // Loading access status
  if (access === null) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--text-muted)' }}>
        Checking access...
      </div>
    );
  }

  // Authenticated but not subscribed – show contact prompt (B2B, no self-serve purchase)
  if (access === false) {
    return (
      <BlurredContent
        message={
          <>
            <div style={{ color: 'var(--text-on-overlay)', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Access to {featureName} requires a subscription
            </div>
            <div style={{ color: 'var(--text-on-overlay-muted)', fontSize: 14, maxWidth: 400, marginBottom: 16 }}>
              {description}
            </div>
          </>
        }
        button={
          <a
            href="mailto:info@drivenby.com.hk?subject=Access%20request"
            style={{
              padding: '10px 24px',
              background: 'var(--accent)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--accent)')}
          >
            Contact us for access
          </a>
        }
        footnote={
          <div
            style={{
              color: 'var(--text-on-overlay-muted)',
              fontSize: 12,
              maxWidth: 400,
              marginTop: 16,
              opacity: 0.85,
            }}
          >
            {corporateNotice || DEFAULT_CORPORATE_NOTICE}
          </div>
        }
      />
    );
  }

  // Access granted – render children normally
  return children;
};

export default SubscriptionGuard;